import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Amplify } from "aws-amplify";
import { fetchAuthSession, signOut as amplifySignOut } from "aws-amplify/auth";
import { Authenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import "bootstrap/dist/css/bootstrap.min.css";

import PiracyBarcode from "./PiracyBarcode";
import Declaration from "./Declaration";
import PiracyCheck from "./PiracyCheck";
import { Context } from "./component/Context_Component/Context";
import { AppProvider } from "./AppContext";

Amplify.configure({
  aws_project_region: "ap-south-1",
  aws_cognito_region: "ap-south-1",
  aws_user_pools_id: "ap-south-1_nVfPrXSsm",
  aws_user_pools_web_client_id: "79adsqna2lajkreool9122o0iq",
});

function ProtectedRoute({ isAllowed, children }) {
  if (!isAllowed) {
    alert("You are not authorized to access this page.");
    return <Navigate to="/" />;
  }
  return children;
}

function App({ signOut, user }) {
  const [userGroup, setUserGroup] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);

  const userEmail =
    user?.attributes?.email ||
    user?.signInDetails?.loginId ||
    "Email not found";

  useEffect(() => {
    const checkGroups = async () => {
      try {
        const session = await fetchAuthSession({ forceRefresh: true });
        const groups = session.tokens?.idToken?.payload["cognito:groups"] || [];
        const lowerGroups = groups.map((g) => g.toLowerCase());

        console.log("User Groups:", lowerGroups);

        if (lowerGroups.includes("admin")) {
          setUserGroup("admin");
        } else {
          alert("Access denied: You are not part of the admin group.");
          await amplifySignOut();
        }
      } catch (error) {
        console.error("Error fetching groups:", error);
        await amplifySignOut();
      } finally {
        setCheckingAuth(false);
      }
    };

    if (user) checkGroups();
  }, [user]);

  if (checkingAuth || !userGroup) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100 bg-light">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const isAdmin = userGroup === "admin";

  return (
    <Context>
      <AppProvider>
        <BrowserRouter>
          <div
            className="d-flex justify-content-between align-items-center px-3 py-2"
            style={{
              background: "#ffffffdd",
              backdropFilter: "blur(10px)",
              boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
              borderBottom: "1px solid #dee2e6",
              position: "sticky",
              top: 0,
              zIndex: 1000,
            }}
          >
            <div>
              <span style={{ fontSize: "1.05rem" }}>
                👋 <strong>Logged in as :</strong>
              </span>
              <div style={{ fontWeight: "600" }}>{userEmail}</div>
            </div>
            <button
              className="btn btn-danger btn-sm d-flex align-items-center gap-2"
              onClick={signOut}
            >
              <i className="bi bi-box-arrow-right" /> Sign Out
            </button>
          </div>

          <div
            className="container-fluid py-4"
            style={{ minHeight: "calc(100vh - 60px)", background: "#f8f9fa" }}
          >
            <div className="row justify-content-center">
              <div className="col-12 col-md-10">
                <div
                  className="card shadow border-0"
                  style={{
                    borderRadius: "20px",
                    padding: "30px 20px",
                    background: "#fff",
                  }}
                >
                  <Routes>
                    <Route
                      path="/"
                      element={
                        <ProtectedRoute isAllowed={isAdmin}>
                          <PiracyBarcode />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/PiracyCheck"
                      element={
                        <ProtectedRoute isAllowed={isAdmin}>
                          <PiracyCheck />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/Declaration"
                      element={
                        <ProtectedRoute isAllowed={isAdmin}>
                          <Declaration />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="*" element={<Navigate to="/" />} />
                  </Routes>
                </div>
              </div>
            </div>
          </div>
        </BrowserRouter>
      </AppProvider>
    </Context>
  );
}

export default function AppWrapper() {
  return (
    <Authenticator
      loginMechanisms={["email"]}
      signUpAttributes={["name"]}
      components={{
        SignUp: {
          FormFields() {
            return <Authenticator.SignUp.FormFields />;
          },
        },
      }}
    >
      {({ signOut, user }) => <App signOut={signOut} user={user} />}
    </Authenticator>
  );
}
